import { ref, toRaw, toRef } from "vue"
import { defineComponent, inferProps, useOnInput, useOnInputNumber, type ReactiveComponent } from "vue-utils"
import { useIncidentHandler } from "../../../IncidentLogHandler"
import EditValueModal from "../EditValueModal"
import { useWatchExternalChanges } from "../useWatchChanges"
import { css } from "vite-css-in-js"
import type { Nullable, SearchPattern } from "@/models"

interface Props {
	searchPattern: Nullable<SearchPattern>
	cancel(): void
	setSearchPattern(searchPattern: SearchPattern): Promise<void>
}

const searchPatternFormStyling = css`
	display: grid;
	grid-template-columns: repeat(2, 1fr);
	gap: 0.5rem 2rem;

	label[data-span="2"] {
		grid-column-start: span 2;
	}

	@media (max-width: 992px) {
		grid-template-columns: 1fr;

		label[data-span="2"] {
			grid-column-start: span 1;
		}
	}
`

const EditSearchPattern: ReactiveComponent<Props> = (props) => {
	const handler = useIncidentHandler()
	const searchPattern = ref<SearchPattern>(props.searchPattern !== null ? {...props.searchPattern} : {commenceSearchPosition: null, firstLegDirection: null, firstTurn: null, creepDirection: null, legLength: null, numberOfLegs: null, trackSpacing: null})

	useWatchExternalChanges({
		name: "Search Pattern",
		currentValue: toRaw(searchPattern),
		globalValue: toRef(props, "searchPattern"),
		equals: (a, b) => {
			a = toRaw(a)
			b = toRaw(b)
			return a?.commenceSearchPosition === b?.commenceSearchPosition &&
				    a?.firstLegDirection === b?.firstLegDirection &&
					 a?.firstTurn === b?.firstTurn &&
					 a?.creepDirection === b?.creepDirection &&
					 a?.legLength === b?.legLength &&
					 a?.numberOfLegs === b?.numberOfLegs &&
					 a?.trackSpacing === b?.trackSpacing
		}
	})

	return () => (
		<EditValueModal
			title={handler.readOnly ? "View Search Pattern " : "Edit Search Pattern"}
			cancel={props.cancel}
			save={() => props.setSearchPattern(searchPattern.value)}
			style={{ width: "64rem" }}
		>
			<div class={searchPatternFormStyling} style={{ minHeight: "20rem" }}>
				<label data-span="2">
					Commence Search Position (CSP)
					<input
						type="text"
						value={searchPattern.value.commenceSearchPosition}
						onInput={useOnInput((newCsp) => (searchPattern.value.commenceSearchPosition = newCsp))}
						disabled={handler.readOnly}
					/>
				</label>
				<label>
					First Leg direction (COG)
					<input
						type="text"
						value={searchPattern.value.firstLegDirection}
						onInput={useOnInput((newCog) => (searchPattern.value.firstLegDirection = newCog))}
						disabled={handler.readOnly}
					/>
				</label>
				<label>
					First Turn
					<input
						type="text"
						value={searchPattern.value.firstTurn}
						onInput={useOnInput((newCog) => (searchPattern.value.firstTurn = newCog))}
						disabled={handler.readOnly}
					/>
				</label>
				<label>
					Creep Direction
					<input
						type="text"
						value={searchPattern.value.creepDirection}
						onInput={useOnInput((newCd) => (searchPattern.value.creepDirection = newCd))}
						disabled={handler.readOnly}
					/>
				</label>
				<label>
					Leg Length
					<input
						type="text"
						value={searchPattern.value.legLength}
						onInput={useOnInput((newLl) => (searchPattern.value.legLength = newLl))}
						disabled={handler.readOnly}
					/>
				</label>
				<label>
					Number of Legs
					<input
						type="number"
						min="0"
						value={searchPattern.value.numberOfLegs}
						onInput={useOnInputNumber((newNol) => (searchPattern.value.numberOfLegs = newNol))}
						disabled={handler.readOnly}
					/>
				</label>
				<label>
					Track Spacing
					<input
						type="text"
						value={searchPattern.value.trackSpacing}
						onInput={useOnInput((newTs) => (searchPattern.value.trackSpacing = newTs))}
						disabled={handler.readOnly}
					/>
				</label>
			</div>
		</EditValueModal>
	)
}

export default defineComponent(EditSearchPattern, inferProps<Props>())
